import { EditionInfoDTO } from '../ports/types';
import { SupportedLocale } from '../i18n/types';
import { i18n } from '../i18n/i18n';

export type EditionType = 'web_free' | 'pc_app_paid' | 'mobile_app_paid';
export type TabType = 'physics' | 'wobble' | 'audio' | 'ambient';
export type WobbleModeType = 'breath' | 'heartbeat' | 'tremble';

export interface UiStateData {
  edition: EditionType;
  locale: SupportedLocale;
  isPanelOpen: boolean;
  isMobilePanelOpen: boolean;
  activeTab: TabType;
  currentPresetId: string;
  currentPresetName: string;
  physics: {
    radiusPercent: number; // 0.05 ~ 0.30 (default: 0.12)
    stiffness: number;     // 0.05 ~ 0.25 (default: 0.15)
    damping: number;       // 0.82 ~ 0.96 (default: 0.91)
    radiusSlider: number;  // 5 ~ 30 (default: 12)
    stiffnessSlider: number; // 10 ~ 90 (default: 50)
    dampingSlider: number;   // 30 ~ 95 (default: 75)
  };
  autoWobble: {
    enabled: boolean;
    mode: WobbleModeType;
    speed: number;
    intensity: number;
    centerX: number;
    centerY: number;
    radiusSlider: number;  // 5 ~ 45 (default: 18)
    radiusPercent: number; // 0.05 ~ 0.45 (default: 0.18)
    showGuide: boolean;    // default: false (auto-wobble during playback hides the pin by default)
  };
  audio: {
    masterVolume: number;
    seVolume: number;
    selectedSE: 'purin' | 'poyon' | 'water';
    isMuted: boolean;
  };
  ambientActive: boolean;
  touchRingVisible: boolean;
}

export type StateListener = (state: UiStateData) => void;

/**
 * フロントエンドUI状態管理ストア (Observer Pattern)
 */
export class UiState {
  private static instance: UiState | null = null;
  private state: UiStateData;
  private listeners: Set<StateListener> = new Set();

  constructor(initialEdition: EditionType = 'web_free') {
    const initialLocale = i18n.getLocale();
    this.state = {
      edition: initialEdition,
      locale: initialLocale,
      isPanelOpen: true,
      isMobilePanelOpen: false,
      activeTab: 'physics',
      currentPresetId: 'p_default',
      currentPresetName: i18n.t().preset.defaultName,
      physics: {
        radiusPercent: 0.12,
        stiffness: 0.15,
        damping: 0.91,
        radiusSlider: 12,
        stiffnessSlider: 50,
        dampingSlider: 75,
      },
      autoWobble: {
        enabled: false,
        mode: 'breath',
        speed: 1.0,
        intensity: 0.3,
        centerX: 0.5,
        centerY: 0.55,
        radiusSlider: 18,
        radiusPercent: 0.18,
        showGuide: false,
      },
      audio: {
        masterVolume: 80,
        seVolume: 80,
        selectedSE: 'purin',
        isMuted: false,
      },
      ambientActive: false,
      touchRingVisible: true,
    };

    i18n.subscribe((locale) => {
      if (this.state.locale !== locale) {
        this.state.locale = locale;
        if (this.state.currentPresetId === 'p_default') {
          this.state.currentPresetName = i18n.t().preset.defaultName;
        }
        this.notify();
      }
    });
  }

  static getInstance(): UiState {
    if (!UiState.instance) {
      UiState.instance = new UiState();
    }
    return UiState.instance;
  }

  getSnapshot(): UiStateData {
    return JSON.parse(JSON.stringify(this.state));
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  // --- エディションポリシー判定 ---
  getEditionInfo(): EditionInfoDTO {
    return {
      edition: this.state.edition,
      maxPresets: this.state.edition === 'web_free' ? 0 : -1,
      isAudioEnabled: this.state.edition !== 'web_free',
      isAutoWobbleEnabled: this.state.edition !== 'web_free',
      isWatermarkForced: this.state.edition === 'web_free',
      isUrlImportEnabled: this.state.edition !== 'web_free',
    };
  }

  isUrlImportAllowed(): boolean {
    return this.state.edition !== 'web_free';
  }

  isPresetSaveAllowed(): boolean {
    return this.state.edition !== 'web_free';
  }

  isExportAllowed(): boolean {
    // Web無料版・有料版ともにエクスポート可能（無料版は透かしあり、有料版は透かしなし）
    return true;
  }

  isWatermarkForced(): boolean {
    return this.state.edition === 'web_free';
  }

  isAutoWobbleAllowed(): boolean {
    return this.state.edition !== 'web_free';
  }

  isAudioAllowed(): boolean {
    return this.state.edition !== 'web_free';
  }

  isAmbientAllowed(): boolean {
    return this.state.edition === 'pc_app_paid';
  }

  // --- 状態更新メソッド群 ---
  setLocale(locale: SupportedLocale): void {
    if (this.state.locale === locale) return;
    this.state.locale = locale;
    i18n.setLocale(locale);
    this.notify();
  }

  setEdition(edition: EditionType): void {
    this.state.edition = edition;
    this.notify();
  }

  setPanelOpen(isOpen: boolean): void {
    this.state.isPanelOpen = isOpen;
    this.notify();
  }

  togglePanel(): void {
    this.state.isPanelOpen = !this.state.isPanelOpen;
    this.notify();
  }

  toggleMobilePanel(): void {
    this.state.isMobilePanelOpen = !this.state.isMobilePanelOpen;
    this.notify();
  }

  setActiveTab(tab: TabType): void {
    this.state.activeTab = tab;
    this.notify();
  }

  setPhysicsSliders(radius: number, stiffness: number, damping: number): void {
    this.state.physics.radiusSlider = radius;
    this.state.physics.stiffnessSlider = stiffness;
    this.state.physics.dampingSlider = damping;

    // inception/mock 準拠の物理定数マッピング式
    this.state.physics.radiusPercent = radius / 100;
    this.state.physics.stiffness = 0.05 + (stiffness / 100) * 0.20;
    this.state.physics.damping = 0.82 + (damping / 100) * 0.14;

    this.notify();
  }

  setPhysicsParams(params: { softness?: number; stiffness?: number; damping?: number }): void {
    const radius = params.softness !== undefined ? Math.round(params.softness / 4) : this.state.physics.radiusSlider;
    const stiffness = params.stiffness ?? this.state.physics.stiffnessSlider;
    const damping = params.damping ?? this.state.physics.dampingSlider;
    this.setPhysicsSliders(radius, stiffness, damping);
    if (params.softness !== undefined) (this.state.physics as any).softness = params.softness;
    if (params.stiffness !== undefined) (this.state.physics as any).stiffness = params.stiffness;
    if (params.damping !== undefined) (this.state.physics as any).damping = params.damping;
  }

  resetPhysicsDefault(): void {
    this.setPhysicsSliders(12, 50, 75);
  }

  setPreset(id: string, name: string): void {
    this.state.currentPresetId = id;
    this.state.currentPresetName = name;
    this.notify();
  }

  setAutoWobble(
    enabledOrObj: boolean | Partial<UiStateData['autoWobble']>,
    mode?: WobbleModeType,
    speed?: number,
    intensity?: number,
    centerX?: number,
    centerY?: number,
    radiusSlider?: number
  ): void {
    if (typeof enabledOrObj === 'object') {
      const obj = enabledOrObj;
      if (obj.enabled !== undefined) this.state.autoWobble.enabled = obj.enabled;
      if (obj.mode) this.state.autoWobble.mode = obj.mode;
      if (obj.speed !== undefined) this.state.autoWobble.speed = obj.speed;
      if (obj.intensity !== undefined) this.state.autoWobble.intensity = obj.intensity;
      if (obj.centerX !== undefined) this.state.autoWobble.centerX = Math.max(0, Math.min(1, obj.centerX));
      if (obj.centerY !== undefined) this.state.autoWobble.centerY = Math.max(0, Math.min(1, obj.centerY));
      if (obj.radiusSlider !== undefined) {
        this.state.autoWobble.radiusSlider = obj.radiusSlider;
        this.state.autoWobble.radiusPercent = obj.radiusSlider / 100;
      }
      if (obj.radiusPercent !== undefined) {
        this.state.autoWobble.radiusPercent = obj.radiusPercent;
        this.state.autoWobble.radiusSlider = Math.round(obj.radiusPercent * 100);
      }
      if (obj.showGuide !== undefined) this.state.autoWobble.showGuide = obj.showGuide;
      this.notify();
      return;
    }

    this.state.autoWobble.enabled = enabledOrObj;
    if (mode) this.state.autoWobble.mode = mode;
    if (speed !== undefined) this.state.autoWobble.speed = speed;
    if (intensity !== undefined) this.state.autoWobble.intensity = intensity;
    if (centerX !== undefined) this.state.autoWobble.centerX = Math.max(0, Math.min(1, centerX));
    if (centerY !== undefined) this.state.autoWobble.centerY = Math.max(0, Math.min(1, centerY));
    if (radiusSlider !== undefined) {
      this.state.autoWobble.radiusSlider = radiusSlider;
      this.state.autoWobble.radiusPercent = radiusSlider / 100;
    }
    this.notify();
  }

  setAutoWobbleCenter(centerX: number, centerY: number): void {
    this.state.autoWobble.centerX = Math.max(0, Math.min(1, centerX));
    this.state.autoWobble.centerY = Math.max(0, Math.min(1, centerY));
    this.notify();
  }

  setAutoWobbleRadius(radiusSlider: number): void {
    this.state.autoWobble.radiusSlider = radiusSlider;
    this.state.autoWobble.radiusPercent = radiusSlider / 100;
    this.notify();
  }

  setAutoWobbleGuide(showGuide: boolean): void {
    this.state.autoWobble.showGuide = showGuide;
    this.notify();
  }

  setAudio(selectedSE?: 'purin' | 'poyon' | 'water', volume?: number, isMuted?: boolean): void {
    if (selectedSE) this.state.audio.selectedSE = selectedSE;
    if (volume !== undefined) this.state.audio.masterVolume = volume;
    if (isMuted !== undefined) this.state.audio.isMuted = isMuted;
    this.notify();
  }

  setAmbientActive(active: boolean): void {
    this.state.ambientActive = active;
    this.notify();
  }

  setTouchRingVisible(visible: boolean): void {
    this.state.touchRingVisible = visible;
    this.notify();
  }
}
